import React, { useState } from 'react'
import { shortenSido } from '../../utils/address'
import type { PlaceSuggestion, RegisterPlaceRequest } from '../api/map'
import { adminApi } from '../api'
import { toastUtil } from '../../utils/toast'
import AdminKakaoPlaceForm from '../components/AdminKakaoPlaceForm'
import AdminPlaceSuggestionStatusBadge from '../components/AdminPlaceSuggestionStatusBadge'

interface AdminPlaceSuggestionDetailModalProps {
  suggestion: PlaceSuggestion
  onClose: () => void
  formatDate: (dateString: string) => string
  /** 처리 후 목록을 갱신하기 위해 부모에 알린다 */
  onProcessed: () => void
}

const AdminPlaceSuggestionDetailModal: React.FC<
  AdminPlaceSuggestionDetailModalProps
> = ({ suggestion: initialSuggestion, onClose, formatDate, onProcessed }) => {
  // 장소를 여러 개 붙일 수 있어서 모달을 닫지 않고 화면을 갱신한다.
  const [suggestion, setSuggestion] = useState(initialSuggestion)
  const [rejectNote, setRejectNote] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  // 등록에 성공하면 입력 폼을 비우기 위해 remount 시킨다.
  const [formKey, setFormKey] = useState(0)

  const registeredPlaces = suggestion.places ?? []
  const isPending = suggestion.status === 'pending'

  const handleRegister = async (data: RegisterPlaceRequest) => {
    try {
      const result = await adminApi.registerPlaceFromSuggestion(
        suggestion.id,
        data
      )
      toastUtil.success(result.message)
      setSuggestion(result.data)
      setFormKey((prev) => prev + 1)
      onProcessed()
    } catch (error: any) {
      toastUtil.error(error?.response?.data?.message || '등록에 실패했습니다')
      // 폼을 비우지 않는다 — 값을 고쳐 다시 시도할 수 있어야 한다.
      throw error
    }
  }

  const handleDetach = async (placeId: number, placeName: string) => {
    setIsUpdating(true)
    try {
      const result = await adminApi.detachPlaceFromSuggestion(
        suggestion.id,
        placeId
      )
      toastUtil.success(`${placeName} 연결 해제`)
      setSuggestion(result.data)
      onProcessed()
    } catch (error: any) {
      toastUtil.error(
        error?.response?.data?.message || '연결을 해제하지 못했습니다'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const handleStatusChange = async (status: 'pending' | 'rejected') => {
    setIsUpdating(true)
    try {
      const result = await adminApi.updatePlaceSuggestionStatus(
        suggestion.id,
        status,
        status === 'rejected' ? rejectNote.trim() : undefined
      )
      toastUtil.success(result.message)
      onProcessed()
      onClose()
    } catch (error: any) {
      toastUtil.error(
        error?.response?.data?.message || '상태를 바꾸지 못했습니다'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const labelClass = 'text-style-labelS text-textColorText2 mb-4px block'

  return (
    <div className="p-24px">
      {/* 제안 내용 */}
      <div className="grid grid-cols-2 gap-16px mb-24px">
        <div>
          <label className={labelClass}>제안 ID</label>
          <div className="text-style-body2 text-textColorText2">
            {suggestion.id}
          </div>
        </div>

        <div>
          <label className={labelClass}>상태</label>
          <AdminPlaceSuggestionStatusBadge status={suggestion.status} />
        </div>

        <div>
          <label className={labelClass}>상호명</label>
          <div className="text-style-body2 text-textColorText2 break-words">
            {suggestion.store_name}
          </div>
        </div>

        <div>
          <label className={labelClass}>지역</label>
          <div className="text-style-body2 text-textColorText2 break-words">
            {suggestion.region}
          </div>
        </div>

        <div>
          <label className={labelClass}>제안한 사용자</label>
          <div className="text-style-body2 text-textColorText2">
            {suggestion.user
              ? `${suggestion.user.nickname} (${suggestion.user.username})`
              : '알 수 없음'}
          </div>
        </div>

        <div>
          <label className={labelClass}>제안일</label>
          <div className="text-style-body2 text-textColorText2">
            {formatDate(suggestion.created_at)}
          </div>
        </div>
      </div>

      {/* 등록된 장소 — 여러 곳일 수 있다 */}
      {registeredPlaces.length > 0 && (
        <div className="mb-24px">
          <div className="flex items-center justify-between mb-8px">
            <h3 className="text-style-labelM text-textColorText2">
              등록된 장소 {registeredPlaces.length}곳
            </h3>
            {suggestion.processed_at && (
              <span className="text-style-labelS text-textColorText3">
                처리일 {formatDate(suggestion.processed_at)}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-8px">
            {registeredPlaces.map((place) => (
              <div
                key={place.id}
                className="flex items-center justify-between gap-12px p-12px rounded-8px bg-backgroundColorBg2 border border-brandPrimary-20%"
              >
                <div className="min-w-0">
                  <div className="text-style-body2 text-textColorText2">
                    {place.name}{' '}
                    <span className="text-textColorText3">(ID {place.id})</span>
                  </div>
                  {place.address && (
                    <div className="text-style-labelS text-textColorText3 mt-2px break-words">
                      {shortenSido(place.address)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDetach(place.id, place.name)}
                  disabled={isUpdating}
                  className={`
                    shrink-0
                    py-6px
                    px-12px
                    rounded-8px
                    text-style-labelS
                    border
                    border-brandPrimary-20%
                    text-textColorText2
                    hover:bg-4B3621-80%-5%
                    transition-colors
                    disabled:cursor-not-allowed
                  `}
                >
                  연결 해제
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestion.status === 'rejected' && (
        <div className="p-16px rounded-8px bg-backgroundColorBg2 border border-brandPrimary-20% mb-24px">
          <div className="text-style-labelS text-textColorText2 mb-4px">
            반려 사유
          </div>
          <div className="text-style-body2 text-textColorText2 break-words">
            {suggestion.admin_note || '(사유 없음)'}
          </div>
          {suggestion.processed_at && (
            <div className="text-style-labelS text-textColorText3 mt-8px">
              처리일 {formatDate(suggestion.processed_at)}
            </div>
          )}
        </div>
      )}

      {/* 반려한 건이 아니면 장소를 (더) 등록할 수 있다 */}
      {suggestion.status !== 'rejected' && (
        <div className="border-t border-brandPrimary-10% pt-24px mb-24px">
          <h3 className="text-style-labelM text-textColorText2 mb-8px">
            {registeredPlaces.length > 0 ? '장소 더 등록' : '장소 등록'}
          </h3>
          <p className="text-style-labelS text-textColorText3 mb-16px">
            같은 상호가 여러 곳에 있어 어디인지 특정할 수 없으면 후보를 모두
            등록해도 됩니다. 사용자가 지도에서 고릅니다.
          </p>
          <AdminKakaoPlaceForm
            key={formKey}
            initialName={suggestion.store_name}
            onSubmit={handleRegister}
            submitLabel={
              registeredPlaces.length > 0 ? '이 장소도 등록' : '장소로 등록'
            }
          />
        </div>
      )}

      {/* 아직 아무것도 등록하지 않았을 때만 반려할 수 있다 */}
      {isPending && (
        <div className="border-t border-brandPrimary-10% pt-24px">
          <h3 className="text-style-labelM text-textColorText2 mb-12px">
            반려
          </h3>
          <div className="flex gap-8px">
            <input
              type="text"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="사유 (예: 폐업, 중복, 정보 부족)"
              className={`
                flex-1
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
                placeholder:text-textColorText3
              `}
            />
            <button
              type="button"
              onClick={() => handleStatusChange('rejected')}
              disabled={isUpdating}
              className={`
                shrink-0
                py-10px
                px-20px
                rounded-8px
                text-style-button2
                border
                border-brandPrimary-20%
                text-textColorText2
                hover:bg-4B3621-80%-5%
                transition-colors
                disabled:cursor-not-allowed
              `}
            >
              반려하기
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-24px pt-24px border-t border-brandPrimary-5%">
        {/* 처리된 제안은 되돌릴 수 있어야 한다. 잘못 처리하는 일이 생기기 때문. */}
        {!isPending ? (
          <button
            onClick={() => handleStatusChange('pending')}
            disabled={isUpdating}
            className={`
              py-10px
              px-20px
              rounded-8px
              text-style-button2
              border
              border-brandPrimary-20%
              text-textColorText2
              hover:bg-4B3621-80%-5%
              transition-colors
              disabled:cursor-not-allowed
            `}
          >
            미처리로 되돌리기
          </button>
        ) : (
          <span />
        )}

        <button
          onClick={onClose}
          className={`
            py-10px
            px-20px
            bg-4B3621-80%
            text-white
            rounded-8px
            text-style-button2
            hover:bg-4B3621-80%-70%
            transition-colors
          `}
        >
          닫기
        </button>
      </div>
    </div>
  )
}

export default AdminPlaceSuggestionDetailModal
